import { useQuery } from "@tanstack/react-query";
import { create } from "zustand";
import HotelReservationServices from "@/services/hotelReservationServices";
import RefundProcessServices from "@/services/refundProcessServices";
import { HotelReservationWithRefundProcess } from "@/types/hotelReservation";
import { getGlobalSessionId } from "@/services";

type HotelReservationsState = {
  reservations: HotelReservationWithRefundProcess[];
  setReservations: (_reservations: HotelReservationWithRefundProcess[]) => void;
};

const useHotelReservationsStore = create<HotelReservationsState>((set) => ({
  reservations: [],
  setReservations: (reservations) => set({ reservations }),
}));

export const useHotelReservations = () => {
  const { setReservations } = useHotelReservationsStore();
  const reservations = useHotelReservationsStore((state) => state.reservations);

  const sessionId = getGlobalSessionId();

  const query = useQuery({
    queryKey: ["hotelReservations"],
    enabled: !!sessionId, // Only run query if sessionId exists
    queryFn: async () => {
      try {
        // Fetch both hotel reservations and refund processes
        const [hotelReservations, refundProcesses] = await Promise.all([
          HotelReservationServices.getReservations(),
          RefundProcessServices.getRefundProcesses(),
        ]);

        // Create a map of refund processes for hotel reservations
        const hotelRefundProcessMap = new Map();
        refundProcesses
          .filter(
            (p: any) => p.type === "HOTEL" && p.referencePk && p.referenceSk
          )
          .forEach((process: any) => {
            const key = `${process.referencePk}#${process.referenceSk}`;
            hotelRefundProcessMap.set(key, process);
          });

        // Map hotel reservations with refund process data
        const reservationsWithRefund: HotelReservationWithRefundProcess[] =
          hotelReservations.map((reservation: any) => {
            const key = `${reservation.pk}#${reservation.sk}`;
            const process = hotelRefundProcessMap.get(key);
            return {
              ...reservation,
              currentStatus: process?.currentStatus || null,
              reasonForIntervention: process?.reason || null,
              refundProcessPk: process?.pk,
              refundProcessSk: process?.sk,
              hotelPrices: null,
              pricesFetchError: null,
            };
          });

        // Fetch hotel prices for reservations that have hotelInfoId
        console.log(
          `Starting price fetch for ${reservationsWithRefund.length} reservations`
        );
        const reservationsWithPrices = await Promise.all(
          reservationsWithRefund.map(async (reservation) => {
            if (!reservation.hotelInfoId) {
              console.log(
                `Skipping price fetch for ${reservation.hotelName} - no hotelInfoId`
              );
              return reservation;
            }

            try {
              const priceResponse =
                await HotelReservationServices.getHotelPrices({
                  hotelId: reservation.hotelInfoId,
                  checkInDate: reservation.checkInDate,
                  checkOutDate: reservation.checkOutDate,
                  noOfRooms: reservation.roomType?.noOfRooms || 1,
                  noOfAdults: reservation.roomType?.noOfAdults || 2,
                });

              if (priceResponse && priceResponse.prices) {
                return {
                  ...reservation,
                  hotelPrices: priceResponse.prices,
                };
              } else {
                console.error(
                  `Failed to fetch prices for ${reservation.hotelName}:`,
                  "No price data received"
                );
                return {
                  ...reservation,
                  pricesFetchError: "No price data received",
                };
              }
            } catch (error) {
              const errorMessage =
                error instanceof Error ? error.message : "Price fetch error";
              console.error(
                `Error fetching prices for hotel ${reservation.hotelName} (ID: ${reservation.hotelInfoId}):`,
                error
              );
              return {
                ...reservation,
                pricesFetchError: errorMessage,
              };
            }
          })
        );

        setReservations(reservationsWithPrices);
        return reservationsWithPrices;
      } catch (error) {
        console.error("🏨 useHotelReservations - Error:", error);
        throw error;
      }
    },
  });

  return {
    reservations,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};
